import React, { useEffect, useMemo, useState } from 'react';
import { Form, Select, DatePicker, Button, Row, Col, Input, Alert } from 'antd';
import CBInputNumber from '../../../components/CBInputNumber';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { livrerTranche, listerLotsDisponibles } from '../../../services/attestationsService';
import CBModal from '../../../components/CBModal';
import { useTranslation } from 'react-i18next';
const { Option } = Select;

/**
 * Props :
 *  - open / onClose
 *  - commande : { commandeTrackingId, numeroCommande, quantite }
 *  - quantiteRestante : nombre d'attestations encore à livrer pour cette commande
 *  - onCreated(tranche)
 */
export default function LivrerTrancheModal({ open, onClose, onCreated, commande, quantiteRestante }) {
  const { t } = useTranslation('attestations');
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [lots, setLots] = useState([]);
  const [lotSel, setLotSel] = useState(null);

  useEffect(() => {
    if (!open) return;
    listerLotsDisponibles().then(setLots).catch(() => setLots([]));
  }, [open]);

  // ─── Filtrer : ne garder que les lots avec stock disponible ───
  const lotsAvecStock = useMemo(
    () => lots.filter(l => Number(l.quantiteRestante) > 0),
    [lots]
  );

  // ─── Détecter la longueur de padding (ex: "0700001" = 7 chars) ───
  const numLen = lotSel ? String(lotSel.numeroDebutSerie || '').length : 0;
  const padNum = (n) => {
    const s = String(n);
    return numLen > 0 && s.length < numLen ? s.padStart(numLen, '0') : s;
  };

  // ─── Calculer le prochain n° de début disponible pour le lot sélectionné ───
  const prochainDebut = useMemo(() => {
    if (!lotSel) return null;
    const fin = Number(lotSel.numeroFinSerie);
    const reste = Number(lotSel.quantiteRestante);
    if (!Number.isFinite(fin) || !Number.isFinite(reste)) return null;
    return fin - reste + 1;
  }, [lotSel]);

  const max = Math.min(quantiteRestante ?? Infinity, lotSel?.quantiteRestante ?? Infinity);

  const onLotChange = (v) => {
    const l = lotsAvecStock.find(x => x.lotTrackingId === v);
    setLotSel(l);
    if (l) {
      const fin = Number(l.numeroFinSerie);
      const reste = Number(l.quantiteRestante);
      const debut = fin - reste + 1;
      form.setFieldsValue({
        numeroDebutSerie: padNum(debut),
        numeroFinSerie: '',
        quantiteLivree: undefined,
      });
    }
  };

  // ─── Auto-calcul du n° de fin selon la quantité ───
  const onValuesChange = (_, all) => {
    if (!lotSel) return;
    const debut = Number(all.numeroDebutSerie);
    const qte = Number(all.quantiteLivree);
    if (Number.isFinite(debut) && Number.isFinite(qte) && qte > 0) {
      const fin = debut + qte - 1;
      const finPad = numLen > 0 ? String(fin).padStart(numLen, '0') : String(fin);
      if (form.getFieldValue('numeroFinSerie') !== finPad) {
        form.setFieldsValue({ numeroFinSerie: finPad });
      }
    }
  };

  // ─── Validation du numéro de début : doit être ≥ prochain disponible ───
  const debutSaisi = Form.useWatch('numeroDebutSerie', form);
  const numeroDejaUtilise = useMemo(() => {
    if (!lotSel || !debutSaisi || prochainDebut == null) return false;
    const n = Number(debutSaisi);
    return Number.isFinite(n) && n < prochainDebut;
  }, [lotSel, debutSaisi, prochainDebut]);

  const onFinish = async (vals) => {
    if (numeroDejaUtilise) {
      toast.error(t('modales.tranche.debutDejaUtilise'));
      return;
    }
    setSubmitting(true);
    try {
      const tranche = await livrerTranche(commande.commandeTrackingId, {
        lotTrackingId: vals.lotTrackingId,
        numeroDebutSerie: String(vals.numeroDebutSerie).trim(),
        numeroFinSerie: String(vals.numeroFinSerie).trim(),
        quantiteLivree: vals.quantiteLivree,
        dateLivraison: vals.dateLivraison?.format('YYYY-MM-DD'),
      });
      toast.success(t('modales.tranche.attribuee', { n: tranche?.quantiteLivree }));
      form.resetFields();
      setLotSel(null);
      onCreated?.(tranche);
      onClose?.();
    } catch (_) {
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CBModal title={t('modales.tranche.titre', { numero: commande?.numeroCommande || '' })}
      open={open}
      onCancel={() => { form.resetFields(); setLotSel(null); onClose?.(); }}
      footer={null}
      width={640}
      destroyOnClose>
      <Form form={form} layout="vertical" onFinish={onFinish}
        onValuesChange={onValuesChange}
        initialValues={{ dateLivraison: dayjs() }}>

        {commande && (
          <Alert type="info" showIcon style={{ marginBottom: 16 }}
            message={t('detail.titre', { numero: commande.numeroCommande })}
            description={t('modales.tranche.quantites', { total: commande.quantite, reste: quantiteRestante ?? '?' })} />
        )}

        {lots.length > 0 && lotsAvecStock.length === 0 && (
          <Alert type="warning" showIcon style={{ marginBottom: 16 }}
            message={t('modales.tranche.tousOctroyes')} />
        )}

        {numeroDejaUtilise && (
          <Alert type="error" showIcon style={{ marginBottom: 16 }}
            message={t('modales.tranche.numeroDejaUtilise')}
            description={t('modales.tranche.numeroDejaUtiliseDescription', { prochain: padNum(prochainDebut) })}
          />
        )}

        <Form.Item name="lotTrackingId" label={t('modales.tranche.lotSource')} rules={[{ required: true }]}>
          <Select placeholder={lotsAvecStock.length === 0 ? t('modales.tranche.aucunLot') : t('modales.tranche.selectionnerLot')}
            onChange={onLotChange}>
            {lotsAvecStock.map(l => (
              <Option key={l.lotTrackingId} value={l.lotTrackingId}>
                {t('modales.tranche.optionLot', { reference: l.referenceLot, reste: l.quantiteRestante, debut: l.numeroDebutSerie, fin: l.numeroFinSerie })}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="numeroDebutSerie" label={t('commun.lot.numeroDebut')} rules={[{ required: true }]}
              extra={lotSel ? t('modales.tranche.prochainDisponible', { n: padNum(prochainDebut) }) : null}
              validateStatus={numeroDejaUtilise ? 'error' : ''}
              help={numeroDejaUtilise ? t('modales.tranche.numeroDejaUtilise') : undefined}>
              <Input placeholder={t('modales.tranche.auto')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="numeroFinSerie" label={t('modales.tranche.finAuto')} rules={[{ required: true }]}>
              <Input placeholder={t('modales.tranche.finPlaceholder')} disabled />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="quantiteLivree" label={t('modales.tranche.quantiteMax', { max: Number.isFinite(max) ? max : '?' })}
              rules={[{ required: true }]}>
              <CBInputNumber min={1} max={Number.isFinite(max) ? max : undefined} step={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="dateLivraison" label={t('modales.tranche.dateLivraison')} rules={[{ required: true }]}>
          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
        </Form.Item>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
          <Button onClick={() => { form.resetFields(); setLotSel(null); onClose?.(); }}>{t('commun.annuler')}</Button>
          <Button type="primary" htmlType="submit" loading={submitting}
            style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}
            disabled={lotsAvecStock.length === 0 || numeroDejaUtilise}>
            {t('modales.tranche.attribuer')}
          </Button>
        </div>
      </Form>
    </CBModal>
  );
}
